export interface ActivityRuntimeConfig {
  ok: true
  version: 1
  application: {
    name: string
    team: string
    officialTeamOwned: boolean
  }
  discord: {
    clientId: string | null
    guildScoped: boolean
    oauthConfigured: boolean
  }
  activity: {
    env: string
    defaultSurface: string
    publicBaseUrlConfigured: boolean
    runtimeConfigured: boolean
    runtimeConnected?: boolean
  }
  surfaces: Array<{
    key: string
    status: 'active' | 'planned'
    route: string
  }>
}

export const fallbackActivityConfig: ActivityRuntimeConfig = {
  ok: true,
  version: 1,
  application: {
    name: 'Striker Arena',
    team: 'AC Tech',
    officialTeamOwned: true
  },
  discord: {
    clientId: null,
    guildScoped: false,
    oauthConfigured: false
  },
  activity: {
    env: 'local',
    defaultSurface: 'home',
    publicBaseUrlConfigured: false,
    runtimeConfigured: false,
    runtimeConnected: false
  },
  surfaces: [
    { key: 'matchroom', status: 'active', route: '/matchroom' },
    { key: 'home', status: 'planned', route: '/home' },
    { key: 'profiles', status: 'planned', route: '/profiles' },
    { key: 'leaderboards', status: 'planned', route: '/leaderboards' },
    { key: 'pro-cs', status: 'planned', route: '/pro-cs' },
    { key: 'inventory', status: 'planned', route: '/inventory' },
    { key: 'demo-review', status: 'planned', route: '/demo-review' },
    { key: 'matchmaking', status: 'planned', route: '/matchmaking' },
    { key: 'teams', status: 'planned', route: '/teams' },
    { key: 'scrims', status: 'planned', route: '/scrims' },
    { key: 'tournaments', status: 'planned', route: '/tournaments' },
    { key: 'map-veto', status: 'planned', route: '/map-veto' },
    { key: 'pickems', status: 'planned', route: '/pickems' },
    { key: 'clips', status: 'planned', route: '/clips' },
    { key: 'coaching', status: 'planned', route: '/coaching' },
    { key: 'seasons', status: 'planned', route: '/seasons' },
    { key: 'hall-of-fame', status: 'planned', route: '/hall-of-fame' },
    { key: 'economy', status: 'planned', route: '/economy' },
    { key: 'reports', status: 'planned', route: '/reports' },
    { key: 'roadmap', status: 'planned', route: '/roadmap' },
    { key: 'server-guide', status: 'planned', route: '/server-guide' },
    { key: 'brand', status: 'planned', route: '/brand' },
    { key: 'source-servers', status: 'planned', route: '/source-servers' },
    { key: 'twitch', status: 'planned', route: '/twitch' },
    { key: 'soundboard', status: 'planned', route: '/soundboard' },
    { key: 'admin', status: 'planned', route: '/admin' }
  ]
}

export async function loadActivityConfig(): Promise<ActivityRuntimeConfig> {
  const url = import.meta.env.PROD
    ? '/api/activity/config'
    : (import.meta.env.VITE_ACTIVITY_CONFIG_URL as string | undefined)

  if (!url) return fallbackActivityConfig

  try {
    const response = await fetch(url, {
      headers: { accept: 'application/json' },
      cache: 'no-store',
      credentials: 'omit',
      signal: AbortSignal.timeout(2_500)
    })
    if (!response.ok) return fallbackActivityConfig
    return normalizeActivityConfig(await response.json())
  } catch {
    return fallbackActivityConfig
  }
}

function normalizeActivityConfig(input: unknown): ActivityRuntimeConfig {
  const root = record(input)
  const app = record(root?.application)
  const discord = record(root?.discord)
  const activity = record(root?.activity)
  return {
    ok: true,
    version: 1,
    application: {
      name: cleanText(app?.name, fallbackActivityConfig.application.name, 48),
      team: cleanText(app?.team, fallbackActivityConfig.application.team, 48),
      officialTeamOwned: Boolean(app?.officialTeamOwned)
    },
    discord: {
      clientId: cleanClientId(discord?.clientId),
      guildScoped: Boolean(discord?.guildScoped),
      oauthConfigured: Boolean(discord?.oauthConfigured)
    },
    activity: {
      env: cleanText(activity?.env, fallbackActivityConfig.activity.env, 24),
      defaultSurface: cleanText(
        activity?.defaultSurface,
        fallbackActivityConfig.activity.defaultSurface,
        32
      ),
      publicBaseUrlConfigured: Boolean(activity?.publicBaseUrlConfigured),
      runtimeConfigured: Boolean(activity?.runtimeConfigured),
      runtimeConnected: Boolean(activity?.runtimeConnected)
    },
    surfaces: normalizeSurfaces(root?.surfaces)
  }
}

function normalizeSurfaces(input: unknown): ActivityRuntimeConfig['surfaces'] {
  if (!Array.isArray(input)) return fallbackActivityConfig.surfaces
  const surfaces = input.slice(0, 32).flatMap((item) => {
    const surface = record(item)
    const key = cleanText(surface?.key, '', 32)
    const route = cleanRoute(surface?.route)
    if (!key || !route) return []
    return [
      {
        key,
        route,
        status: surface?.status === 'active' ? 'active' : 'planned'
      } as const
    ]
  })
  return surfaces.length ? surfaces : fallbackActivityConfig.surfaces
}

function record(input: unknown): Record<string, unknown> | null {
  return input && typeof input === 'object' && !Array.isArray(input)
    ? (input as Record<string, unknown>)
    : null
}

function cleanText(input: unknown, fallback: string, maxLength: number): string {
  if (typeof input !== 'string') return fallback
  const clean = input.replace(/\s+/g, ' ').trim().slice(0, maxLength)
  return clean || fallback
}

function cleanClientId(input: unknown): string | null {
  return typeof input === 'string' && /^\d{6,30}$/.test(input) ? input : null
}

function cleanRoute(input: unknown): string | null {
  if (typeof input !== 'string') return null
  const route = input.trim()
  return /^\/[a-z0-9/-]{0,60}$/i.test(route) ? route : null
}
